import React, { useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { arePricesLoading, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import useQueryParams from 'hooks/useQueryParams'
import useLPCUniqueOptions from 'hooks/OfferPage/useLPCUniqueOptions'
import { initialRooms } from 'lib/offer/occupancyUtils'
import uuidV4 from 'lib/string/uuidV4Utils'
import AlwaysOnRateOptionMobile from 'components/OfferPage/AlwaysOn/MobileBookingFlow/AlwaysOnRateOptionMobile'
import BedbankRoomOptionRate from 'components/OfferPage/Bedbank/BedbankRoomOptionRate'
import { getCombinedRates } from '../SinglePropertyUtils'

interface Props {
  offer: App.Offer;
  bedbankOffer?: App.BedbankOffer;
  packageViews: Array<OfferPackageOptionView>;
  bedbankViews: Array<App.BedbankOfferPackageView>;
  checkIn?: string;
  rooms: Array<App.Occupants>;
  flightDestination?: App.Airport;
  previewMode: boolean;
  roomRateId?: string;
  pricesLoading?: boolean;
  duration?: number;
  airport?: App.Airport;
  roomListId: string;
}

function PropertyRoomOptionMobile(props: Props) {
  const {
    offer,
    bedbankOffer,
    packageViews,
    checkIn,
    rooms,
    flightDestination,
    previewMode,
    roomRateId,
    pricesLoading,
    duration,
    bedbankViews,
    roomListId,
  } = props

  const utmContent = useQueryParams().get('utm_content')

  const options = useMemo(() => {
    if (previewMode) {
      // Preview mode is for content people and will show unavailable rates so they can validate them
      // as the rates may not have had prices and such loaded yet
      return packageViews
    }
    const requiresRates = offer.property?.useDynamicCancellationPolicies && !!checkIn && !pricesLoading
    return packageViews.filter(view => view.capacityValid && view.hotelAvailable && (!requiresRates || view.hasAvailableRates))
  }, [checkIn, offer, packageViews, previewMode, pricesLoading])

  const availableOptions = useLPCUniqueOptions(
    offer.id,
    options,
    {
      useDynamicCancellationPolicies: offer.property?.useDynamicCancellationPolicies ?? false,
      duration,
      checkIn,
      rooms: rooms ?? initialRooms,
    },
  )

  const combinedRates = useMemo(() => getCombinedRates(packageViews, bedbankViews, availableOptions, { previewMode, utmContent, roomRateId }), [availableOptions, bedbankViews, packageViews, previewMode, roomRateId, utmContent])

  const rateListId = useMemo(() => uuidV4(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [bedbankViews]) // regenerate id if packages change

  return (
    <>
      {combinedRates.map((view, index) => {
        if (view.lpcRate) {
          return <AlwaysOnRateOptionMobile
            key={view.lpcRate.package.uniqueKey}
            offer={offer}
            optionNumber={index + 1}
            packageView={view.lpcRate}
            flightDestination={flightDestination}
          />
        } else if (view.bedbankRate && bedbankOffer) {
          return <BedbankRoomOptionRate
            key={view.bedbankRate.rate.id}
            offer={bedbankOffer}
            packageView={view.bedbankRate.view}
            rate={view.bedbankRate.rate}
            optionNumber={index + 1}
            roomPosition={index + 1}
            roomListId={roomListId}
            rateListId={rateListId}
          />
        }
        return null
      })}
    </>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  duration: state.duration,
  previewMode: appState.system.previewMode,
  roomRateId: state.roomOption?.roomRateId,
  pricesLoading: arePricesLoading(appState, state),
  airport: state.airport,
}))(PropertyRoomOptionMobile)
